import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { resolveLandingPath, SUPER_ADMIN_HOME } from "./landing";

/**
 * RouteGuard keeps each kind of account inside the area that belongs to it.
 *
 * The flags come from `user` alone. They used to fall back to decoding the
 * access token, which is stale between renewals — the server resolves both
 * on every /auth/current-user call, so `user` is both fresher and
 * authoritative.
 *
 * `isPending` is not one of the flags checked here. An AD-provisioned user
 * gets a real default role at creation (see `get_or_create_user` on the
 * server) and is permission-gated like everyone else from their first
 * login; `isPending` only marks "not yet reviewed" for the admin's
 * pending-users list, it does not restrict what the account itself can see.
 */
const RouteGuard = () => {
  const location = useLocation();
  const { isAuthenticated, user, isLoading, authChecked } = useAuth();

  // AuthGuard owns the loading and unauthenticated cases; it wraps this one.
  if (isLoading || !authChecked) return null;
  if (!isAuthenticated) return <Outlet />;

  const { pathname } = location;

  // SuperAdmins live entirely under /super-admin/*.
  if (user?.isSuperAdmin) {
    return pathname.startsWith("/super-admin") ? <Outlet /> : <Navigate to={SUPER_ADMIN_HOME} replace />;
  }

  // A tenant user must not wander into the SuperAdmin console. Those routes
  // are permission-gated too, so this changes nothing about what they could
  // reach — it just sends them somewhere useful instead of to "Unauthorized".
  if (pathname.startsWith("/super-admin")) {
    return <Navigate to={resolveLandingPath(user)} replace />;
  }

  return <Outlet />;
};

export default RouteGuard;
